/* =========================================================
   Affiche TENNIS DE TABLE « Ma raquette » — les deux faces réglementaires.
   API : SNTTMateriel.render(data, opts) -> string SVG
     data : { mode:'raquette', titre, kicker, club, joueur, date,
              faceCoupDroit : 'couleur' | 'noir'  (l'autre face prend le noir)
              couleurFace   : 'rouge' | 'bleu' | 'rose' | 'vert' | 'violet'
              bois, coupDroit, revers  → la barre de caractéristiques
              prise, style             → la ligne de sous-titre }
     opts : { palette }

   Depuis la révision ITTF de 2021 (règle 2.4.6), le règlement impose une face
   NOIRE et l'autre d'une couleur homologuée — rouge, bleu, rose, vert ou
   violet. C'est ce que dessine cette affiche, et le détail que tout pongiste
   reconnaît. Ces couleurs sont des couleurs d'OBJET, pas de thème : elles ne
   suivent donc pas la palette.

   Auto-suffisant (aucun fetch, aucune image) → re-render serveur direct.
   ========================================================= */
(function () {
  const PW = 800, PH = 1066, MX = 64;

  const PALETTES = {
    craie: { label:'Craie',      bg:'#F7F4ED', ink:'#0B2D5B', muted:'#8B94A3', accent:'#FF6B2C', line:'#E3DDD0', card:'#FFFFFF', table:'#1F4E8C', deep:'#12365F' },
    bleu:  { label:'Bleu salle', bg:'#0E2A4A', ink:'#F4F1E8', muted:'#8FA6BF', accent:'#FF6B2C', line:'#1C4470', card:'#15355C', table:'#1F4E8C', deep:'#0A1F38' },
    vert:  { label:'Vert table', bg:'#10362C', ink:'#F4F1E8', muted:'#8FB3A5', accent:'#FF6B2C', line:'#1C5544', card:'#164536', table:'#1B5E4A', deep:'#0B271F' },
    nuit:  { label:'Nuit',       bg:'#0B2D5B', ink:'#F7F4ED', muted:'#93A1B8', accent:'#FF9E5C', line:'#1E4272', card:'#12355F', table:'#123E70', deep:'#082444' },
  };

  const NOIR = '#1B1B1E', BOIS = '#D8B98C', BOIS_OMBRE = '#B8965F';
  /* Table FERMÉE : la valeur finit dans un attribut fill. Une saisie client
     recopiée telle quelle permettrait d'injecter du SVG côté serveur. */
  const COULEURS = { rouge:'#C4372B', bleu:'#1F5FBF', rose:'#E3538E', vert:'#1F9B58', violet:'#7A4BBF' };

  const esc = s => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const up = s => String(s == null ? '' : s).toUpperCase();
  const fit = (t, maxW, maxS, cwf, ls, minS) => { const n = String(t).length || 1; minS = minS || 12;
    for (let s = maxS; s > minS; s -= 1) if (n * (cwf * s + ls) <= maxW) return s; return minS; };
  const T = (x,y,size,fill,txt,o) => { o = o || {};
    return `<text x="${x}" y="${y}" font-family="${o.f||'Sora'}" font-size="${size}" font-weight="${o.w||600}" fill="${fill}"`
      + (o.ls?` letter-spacing="${o.ls}"`:'') + ` text-anchor="${o.a||'middle'}"` + (o.op?` opacity="${o.op}"`:'') + `>${txt}</text>`; };

  function stats(p, items, y, x0) {
    x0 = x0 || 54;
    const it = items.filter(x => String(x[0] || '').trim());
    if (!it.length) return '';
    const w = (PW - 2*x0) / it.length;
    let s = `<line x1="${x0}" y1="${y-34}" x2="${PW-x0}" y2="${y-34}" stroke="${p.line}" stroke-width="1.5"/>`;
    it.forEach((c, i) => {
      const cc = x0 + w*i + w/2;
      if (i) s += `<line x1="${(x0+w*i).toFixed(0)}" y1="${y-14}" x2="${(x0+w*i).toFixed(0)}" y2="${y+34}" stroke="${p.line}" stroke-width="1.5"/>`;
      s += T(cc, y+6, fit(c[0], w-24, 34, 0.56, 0, 16), p.ink, esc(c[0]), { f:'Oswald', w:700 })
        + T(cc, y+30, 12, p.muted, esc(up(c[1])), { w:600, ls:2.5 });
    });
    return s;
  }

  /** Raquette vue de face : bois apparent en bordure, revêtement au centre,
      manche évasé. r = rayon du bois. */
  function raquette(cx, cy, r, revetement) {
    const hy = cy + r * 0.95;              /* naissance du manche */
    const hb = cy + r * 2.06;              /* bas du manche */
    return `<ellipse cx="${cx}" cy="${cy}" rx="${r}" ry="${(r*1.09).toFixed(1)}" fill="${BOIS}"/>`
      + `<ellipse cx="${cx}" cy="${cy}" rx="${(r*0.895).toFixed(1)}" ry="${(r*0.985).toFixed(1)}" fill="${revetement}"/>`
      + `<path d="M${(cx-r*0.20).toFixed(1)} ${hy.toFixed(1)} L${(cx+r*0.20).toFixed(1)} ${hy.toFixed(1)} L${(cx+r*0.27).toFixed(1)} ${hb.toFixed(1)} Q${cx} ${(hb+r*0.20).toFixed(1)} ${(cx-r*0.27).toFixed(1)} ${hb.toFixed(1)} Z" fill="${BOIS}"/>`
      + `<path d="M${(cx-r*0.20).toFixed(1)} ${hy.toFixed(1)} L${(cx-r*0.06).toFixed(1)} ${hy.toFixed(1)} L${(cx-r*0.11).toFixed(1)} ${(hb+r*0.06).toFixed(1)} L${(cx-r*0.27).toFixed(1)} ${hb.toFixed(1)} Z" fill="${BOIS_OMBRE}" opacity="0.65"/>`;
  }

  function balle(cx, cy, r) {
    return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="${r}" fill="#F5A623"/>`
      + `<path d="M${(cx-r).toFixed(1)} ${cy.toFixed(1)} a ${r} ${r} 0 0 0 ${r*2} 0" fill="none" stroke="#00000022" stroke-width="${(r*0.10).toFixed(2)}"/>`
      + `<circle cx="${(cx-r*0.32).toFixed(1)}" cy="${(cy-r*0.34).toFixed(1)}" r="${(r*0.20).toFixed(1)}" fill="#ffffff" opacity="0.45"/>`;
  }

  function render(data, opts) {
    data = data || {}; opts = opts || {};
    const p = PALETTES[opts.palette] || PALETTES.craie;
    const cx = PW / 2;

    const titre = up(data.titre) || 'MA RAQUETTE';
    const ts = fit(titre, 690, 74, 0.54, 2, 32);
    const kicker = up(data.kicker) || up(data.club) || 'MON MATÉRIEL';
    const sous = [up(data.prise), up(data.style)].filter(Boolean).join(' · ');

    /* Repli obligatoire : une valeur inconnue (ancienne commande en 'rouge',
       saisie fantaisiste) retombe sur le rouge, jamais sur du vide. */
    const teinte = COULEURS[String(data.couleurFace || 'rouge').toLowerCase()] || COULEURS.rouge;
    const cdNoir = String(data.faceCoupDroit || 'couleur').toLowerCase() === 'noir';
    const corps = raquette(250, 520, 118, cdNoir ? NOIR : teinte)
      + raquette(550, 520, 118, cdNoir ? teinte : NOIR)
      + balle(cx, 700, 19)
      + T(250, 812, 14, p.muted, 'COUP DROIT', { w:700, ls:3 })
      + T(550, 812, 14, p.muted, 'REVERS', { w:700, ls:3 });

    /* 916 et non 946 : les libellés du bandeau descendent à +30, le nom du
       joueur suit à 986 — en dessous, les deux lignes se touchent. */
    const barre = stats(p, [[data.bois, 'Bois'], [data.coupDroit, 'Coup droit'], [data.revers, 'Revers']], 916);

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PW} ${PH}" font-family="'Sora','Inter',sans-serif">
<rect width="${PW}" height="${PH}" fill="${p.bg}"/>
${T(cx, 84, 19, p.accent, esc(kicker), { w:700, ls:5 })}
${T(cx, 168, ts, p.ink, esc(titre), { f:'Oswald', w:700, ls:2 })}
${sous ? T(cx, 204, fit(sous, 660, 17, 0.66, 3.5, 11), p.muted, esc(sous), { w:600, ls:3.5 }) : ''}
${corps}
${barre}
${data.joueur ? T(cx, 986, 19, p.ink, esc(up(data.joueur)), { f:'Oswald', w:700, ls:2 }) : ''}
${data.date ? T(cx, 1016, 13, p.muted, esc(up(data.date)), { w:600, ls:4 }) : ''}
</svg>`;
  }

  window.SNTTMateriel = { render, PALETTES };
})();
